import {
  collection,
  getDocs,
  query,
  where,
  or,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import { mapPropertyDoc, getPropertySortTime, toJsDate } from '../utils/firestoreDates';
import { slugify } from '../utils/seo';
import {
  CATALOG_NS,
  fetchCachedCatalog,
  invalidateCatalog,
  getCachedCatalog,
} from './catalogCache';

export function isPublicListableProperty(prop) {
  return prop.isAvailable !== false && prop.isInactive !== true;
}

export function buildCondominiumPropertyCounts(properties) {
  const counts = {};
  (properties || []).forEach((prop) => {
    if (prop.condominiumId) {
      counts[prop.condominiumId] = (counts[prop.condominiumId] || 0) + 1;
    }
  });
  return counts;
}

export function sortPropertyList(list, orderByValue) {
  if (!list?.length || !orderByValue) return list || [];

  const sorted = [...list];
  switch (orderByValue) {
    case 'price_asc':
      return sorted.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
    case 'price_desc':
      return sorted.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
    case 'newest':
      return sorted.sort((a, b) => getPropertySortTime(b) - getPropertySortTime(a));
    case 'oldest':
      return sorted.sort((a, b) => getPropertySortTime(a) - getPropertySortTime(b));
    case 'area_asc':
      return sorted.sort((a, b) => parseFloat(a.area) - parseFloat(b.area));
    case 'area_desc':
      return sorted.sort((a, b) => parseFloat(b.area) - parseFloat(a.area));
    case 'views_desc':
      return sorted.sort((a, b) => (b.views || 0) - (a.views || 0));
    case 'views_asc':
      return sorted.sort((a, b) => (a.views || 0) - (b.views || 0));
    case 'relevance':
      return sorted.sort((a, b) => {
        if (a.isFeatured && !b.isFeatured) return -1;
        if (!a.isFeatured && b.isFeatured) return 1;
        return getPropertySortTime(b) - getPropertySortTime(a);
      });
    default:
      return sorted;
  }
}

/**
 * Aplica filtros da listagem pública (PropertyList) em memória.
 * @returns {{ filtered: Array, cities: string[], neighborhoods: string[], counts: Object }}
 */
export function applyPublicPropertyFilters(allProperties, activeFilters = {}) {
  const listable = (allProperties || []).filter(isPublicListableProperty);
  const counts = buildCondominiumPropertyCounts(listable);
  const cities = [...new Set(listable.map((prop) => prop.city))]
    .filter(Boolean)
    .sort();

  let filteredList = listable;

  if (activeFilters.location) {
    const locSlug = slugify(activeFilters.location);
    filteredList = filteredList.filter(
      (prop) => prop.city && slugify(prop.city) === locSlug
    );
  }

  if (activeFilters.purpose) {
    filteredList = filteredList.filter((prop) => prop.purpose === activeFilters.purpose);
  }

  if (activeFilters.condominiumLink === 'true') {
    filteredList = filteredList.filter((prop) => Boolean(prop.condominiumId));
    if (activeFilters.condominiumId) {
      filteredList = filteredList.filter(
        (prop) => prop.condominiumId === activeFilters.condominiumId
      );
    }
  } else if (activeFilters.condominiumLink === 'false') {
    filteredList = filteredList.filter((prop) => !prop.condominiumId);
  } else if (activeFilters.condominiumId) {
    filteredList = filteredList.filter(
      (prop) => prop.condominiumId === activeFilters.condominiumId
    );
  }

  if (activeFilters.type) {
    filteredList = filteredList.filter((prop) => prop.type === activeFilters.type);
  }

  if (activeFilters.search) {
    const searchLower = activeFilters.search.toLowerCase();
    filteredList = filteredList.filter((prop) => {
      if (prop.code?.toLowerCase().includes(searchLower)) return true;
      if (prop.title?.toLowerCase().includes(searchLower)) return true;
      if (prop.city?.toLowerCase().includes(searchLower)) return true;
      if (prop.neighborhood?.toLowerCase().includes(searchLower)) return true;
      return false;
    });
  }

  let neighborhoods = [];
  if (activeFilters.location) {
    const locSlug = slugify(activeFilters.location);
    const cityProperties = filteredList.filter(
      (prop) => prop.city && slugify(prop.city) === locSlug
    );
    neighborhoods = [...new Set(cityProperties.map((prop) => prop.neighborhood))]
      .filter(Boolean)
      .sort();
  }

  if (activeFilters.neighborhood) {
    const selectedNeighborhoods = activeFilters.neighborhood.split(',').filter(Boolean);
    if (selectedNeighborhoods.length > 0) {
      const selectedSlugs = selectedNeighborhoods.map((n) => slugify(n));
      filteredList = filteredList.filter(
        (prop) =>
          selectedNeighborhoods.includes(prop.neighborhood) ||
          selectedSlugs.includes(slugify(prop.neighborhood))
      );
    }
  }

  if (activeFilters.minPrice) {
    filteredList = filteredList.filter(
      (prop) => prop.price >= parseFloat(activeFilters.minPrice)
    );
  }
  if (activeFilters.maxPrice) {
    filteredList = filteredList.filter(
      (prop) =>
        activeFilters.maxPrice === '' || prop.price <= parseFloat(activeFilters.maxPrice)
    );
  }

  if (activeFilters.minArea) {
    filteredList = filteredList.filter(
      (prop) => prop.area >= parseFloat(activeFilters.minArea)
    );
  }
  if (activeFilters.maxArea) {
    filteredList = filteredList.filter(
      (prop) =>
        activeFilters.maxArea === '' || prop.area <= parseFloat(activeFilters.maxArea)
    );
  }

  if (activeFilters.bedrooms) {
    filteredList = filteredList.filter(
      (prop) => parseInt(prop.bedrooms, 10) >= parseInt(activeFilters.bedrooms, 10)
    );
  }
  if (activeFilters.bathrooms) {
    filteredList = filteredList.filter(
      (prop) => parseInt(prop.bathrooms, 10) >= parseInt(activeFilters.bathrooms, 10)
    );
  }
  if (activeFilters.parkingSpaces) {
    filteredList = filteredList.filter(
      (prop) => parseInt(prop.parkingSpaces, 10) >= parseInt(activeFilters.parkingSpaces, 10)
    );
  }
  if (activeFilters.suites) {
    filteredList = filteredList.filter(
      (prop) => parseInt(prop.suites, 10) >= parseInt(activeFilters.suites, 10)
    );
  }

  if (activeFilters.isGatedCommunity !== '' && activeFilters.isGatedCommunity != null) {
    filteredList = filteredList.filter((prop) => {
      const hasCondoFee = parseFloat(prop.condoFee) > 0;
      const hasCondoDetails = Object.values(prop.condoDetails || {}).some(
        (value) => value === true
      );
      const isGated = hasCondoFee || hasCondoDetails;
      return activeFilters.isGatedCommunity === 'true' ? isGated : !isGated;
    });
  }

  if (activeFilters.propertyStatus) {
    filteredList = filteredList.filter((prop) => {
      switch (activeFilters.propertyStatus) {
        case 'reserved':
          return prop.isReserved === true || prop.displayTag === 'reserved';
        case 'featured':
          return (
            prop.showInHome === true ||
            prop.isFeatured === true ||
            prop.displayTag === 'featured'
          );
        case 'launch':
          return prop.displayTag === 'launch';
        case 'preLaunch':
          return prop.displayTag === 'preLaunch';
        default:
          return true;
      }
    });
  }

  filteredList = sortPropertyList(filteredList, activeFilters.orderBy || 'newest');

  return {
    filtered: filteredList,
    cities,
    neighborhoods,
    counts,
    listable,
  };
}

function belongsToBroker(prop, uid) {
  return (
    prop.brokerId === uid || prop.createdBy === uid || prop.assignedTo === uid
  );
}

async function loadAllPropertiesFromFirestore() {
  const snapshot = await getDocs(collection(db, 'properties'));
  return snapshot.docs.map(mapPropertyDoc);
}

/** Só imóveis visíveis no site (bate com as security rules públicas). */
async function loadPublicPropertiesFromFirestore() {
  const publicQuery = query(
    collection(db, 'properties'),
    where('isAvailable', '==', true),
    where('isInactive', '==', false)
  );
  const snapshot = await getDocs(publicQuery);
  return snapshot.docs.map(mapPropertyDoc);
}

function mapCondoDoc(d) {
  const data = d.data();
  return {
    id: d.id,
    ...data,
    createdAt: toJsDate(data.createdAt),
    updatedAt: toJsDate(data.updatedAt),
  };
}

async function loadAllCondominiumsFromFirestore() {
  const snap = await getDocs(collection(db, 'condominiums'));
  return snap.docs.map(mapCondoDoc);
}

async function loadPublicCondominiumsFromFirestore() {
  const publicQuery = query(
    collection(db, 'condominiums'),
    where('isAvailable', '==', true),
    where('isInactive', '==', false)
  );
  const snap = await getDocs(publicQuery);
  return snap.docs.map(mapCondoDoc);
}

async function loadBrokerPropertiesFromFirestore(uid) {
  const propertiesRef = collection(db, 'properties');
  try {
    const brokerQuery = query(
      propertiesRef,
      or(
        where('brokerId', '==', uid),
        where('createdBy', '==', uid),
        where('assignedTo', '==', uid)
      )
    );
    const snapshot = await getDocs(brokerQuery);
    const byId = new Map();
    snapshot.docs.forEach((d) => {
      byId.set(d.id, mapPropertyDoc(d));
    });
    return Array.from(byId.values());
  } catch {
    const all = await loadAllPropertiesFromFirestore();
    return all.filter((prop) => belongsToBroker(prop, uid));
  }
}

function propertiesScopeKey({ scope = 'public', uid = '', role = '' } = {}) {
  if (scope === 'admin' && role === 'broker' && uid) {
    return `admin:broker:${uid}`;
  }
  if (scope === 'admin') {
    return 'admin:all';
  }
  return 'public:all';
}

/**
 * Catálogo de imóveis com cache.
 * @param {{ scope?: 'public'|'admin', uid?: string, role?: string, force?: boolean }} options
 */
export async function fetchPropertiesCatalog(options = {}) {
  const { scope = 'public', uid = '', role = '', force = false } = options;
  const scopeKey = propertiesScopeKey({ scope, uid, role });

  return fetchCachedCatalog(
    CATALOG_NS.PROPERTIES,
    scopeKey,
    async () => {
      if (scope === 'admin' && role === 'broker' && uid) {
        return loadBrokerPropertiesFromFirestore(uid);
      }
      if (scope === 'admin') {
        return loadAllPropertiesFromFirestore();
      }
      return loadPublicPropertiesFromFirestore();
    },
    { force }
  );
}

/**
 * Destaques da home: query direta por showInHome (evita baixar o catálogo público inteiro).
 * @param {number} [max=8]
 */
export async function fetchHomeFeaturedProperties(max = 8) {
  try {
    const homeQuery = query(
      collection(db, 'properties'),
      where('showInHome', '==', true),
      limit(Math.max(max * 2, 16))
    );
    const snapshot = await getDocs(homeQuery);
    return snapshot.docs
      .map(mapPropertyDoc)
      .filter((prop) => prop.isAvailable !== false && prop.isInactive !== true)
      .slice(0, max);
  } catch (error) {
    console.warn('fetchHomeFeaturedProperties fallback para catálogo:', error?.message || error);
    const all = await fetchPropertiesCatalog({ scope: 'public' });
    return all
      .filter((prop) => prop.showInHome === true && prop.isInactive !== true && prop.isAvailable !== false)
      .slice(0, max);
  }
}

/**
 * Unidades públicas de um condomínio (evita baixar o catálogo inteiro no detalhe).
 * Usa cache do catálogo quando disponível; senão query direta + fallback.
 * @param {string} condominiumId
 * @param {number} [max=50]
 */
export async function fetchPublicPropertiesByCondominiumId(condominiumId, max = 50) {
  if (!condominiumId) return [];

  const cached = getCachedCatalog(CATALOG_NS.PROPERTIES, 'public:all');
  if (cached?.data?.length) {
    return cached.data
      .filter((unit) => unit.condominiumId === condominiumId)
      .filter(isPublicListableProperty)
      .slice(0, max);
  }

  try {
    const unitsQuery = query(
      collection(db, 'properties'),
      where('condominiumId', '==', condominiumId),
      where('isAvailable', '==', true),
      where('isInactive', '==', false),
      limit(Math.max(max, 1))
    );
    const snapshot = await getDocs(unitsQuery);
    return snapshot.docs.map(mapPropertyDoc).slice(0, max);
  } catch (error) {
    console.warn(
      'fetchPublicPropertiesByCondominiumId fallback para catálogo:',
      error?.message || error
    );
    const all = await fetchPropertiesCatalog({ scope: 'public' });
    return all
      .filter((unit) => unit.condominiumId === condominiumId)
      .filter(isPublicListableProperty)
      .slice(0, max);
  }
}

export function invalidatePropertiesCatalog(scopeKey) {
  invalidateCatalog(CATALOG_NS.PROPERTIES, scopeKey);
}

/** Invalida todos os escopos de properties (após create/update/delete). */
export function invalidateAllPropertiesCatalogs() {
  invalidateCatalog(CATALOG_NS.PROPERTIES);
}

/**
 * Catálogo de condomínios.
 */
export async function fetchCondominiumsCatalog(options = {}) {
  const { uid = '', role = '', force = false, scope = 'public' } = options;
  const isAdminRole =
    scope === 'admin' ||
    role === 'admin' ||
    role === 'ADM' ||
    role === 'ADMIN';
  const scopeKey =
    role === 'broker' && uid
      ? `admin:broker:${uid}`
      : isAdminRole
        ? 'admin:all'
        : 'public:all';

  return fetchCachedCatalog(
    CATALOG_NS.CONDOMINIUMS,
    scopeKey,
    async () => {
      if (role === 'broker' && uid) {
        try {
          const brokerQuery = query(
            collection(db, 'condominiums'),
            or(
              where('brokerId', '==', uid),
              where('createdBy', '==', uid),
              where('assignedTo', '==', uid)
            )
          );
          const brokerSnap = await getDocs(brokerQuery);
          const byId = new Map();
          brokerSnap.docs.forEach((d) => {
            byId.set(d.id, mapCondoDoc(d));
          });
          return Array.from(byId.values());
        } catch {
          const all = await loadAllCondominiumsFromFirestore();
          return all.filter((p) => belongsToBroker(p, uid));
        }
      }

      if (isAdminRole) {
        return loadAllCondominiumsFromFirestore();
      }

      return loadPublicCondominiumsFromFirestore();
    },
    { force }
  );
}

export function invalidateCondominiumsCatalog() {
  invalidateCatalog(CATALOG_NS.CONDOMINIUMS);
}

/**
 * Catálogo de posts do blog (ordenado por createdAt desc).
 */
export async function fetchBlogPostsCatalog({ force = false } = {}) {
  return fetchCachedCatalog(
    CATALOG_NS.BLOG_POSTS,
    'all',
    async () => {
      const postsRef = collection(db, 'blogPosts');
      const baseQuery = query(postsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(baseQuery);
      return snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          createdAt: toJsDate(data.createdAt) || new Date(),
          updatedAt: toJsDate(data.updatedAt),
        };
      });
    },
    { force }
  );
}

export function invalidateBlogPostsCatalog() {
  invalidateCatalog(CATALOG_NS.BLOG_POSTS);
}
